// ===========================================================================
// The keepsake, client side: what Lumi remembers on this device for somebody
// without an account.
//
// The server issues a random key once the age gate has passed (see
// server/src/keepsakes.js). It lives in localStorage, goes back with every
// /api/session call, and that is all it does: it holds no name and no personal
// detail, and clearing it is the whole of "forget me on this device". Every
// storage access is wrapped, because localStorage is missing or throws in
// private windows, some embedded browsers, and the thumbnail capture.
//
// A keepsake may be NAMED (server/src/keepsake-auth.js): a username and a
// password put on it, so the same keepsake -- the same regulars, the same
// messages -- can be signed in to from another device, or got back after
// this one loses the key. Signing in stores the key the server issues to
// this device, in place of whatever was here. The identity is still the
// keepsake: no account, nothing to buy.
//
// No config import here on purpose: this module runs under node --test, and
// config.js reads import.meta.env. Callers pass the server base in. ASCII only.
// ===========================================================================

const KEEPSAKE_KEY = 'lumi.keepsake';

function valid(v) {
  return !!v && typeof v === 'object' && typeof v.id === 'string' && /^ks_/.test(v.id) && typeof v.key === 'string' && /^kk_/.test(v.key);
}

/** @returns {{ id: string, key: string }|null} */
export function getKeepsake() {
  try {
    const v = JSON.parse(localStorage.getItem(KEEPSAKE_KEY) || 'null');
    return valid(v) ? { id: v.id, key: v.key } : null;
  } catch {
    return null;
  }
}

/** Keep what the server just issued. Anything malformed is ignored, never half-stored. */
export function storeKeepsake(keepsake) {
  if (!valid(keepsake)) return false;
  try {
    localStorage.setItem(KEEPSAKE_KEY, JSON.stringify({ id: keepsake.id, key: keepsake.key }));
    return true;
  } catch {
    return false;
  }
}

export function clearKeepsake() {
  try {
    localStorage.removeItem(KEEPSAKE_KEY);
  } catch {
    /* nothing to clear, or nowhere to clear it from */
  }
}

/**
 * "Forget me on this device": tell the server to revoke the key and run every
 * cascade behind it, then clear it here. Storage is cleared even when the
 * request fails -- the device forgets regardless, and a key the server never
 * heard from again is swept after ninety days -- with one exception: a
 * server that REFUSES because a ban stands (403 banned) has kept the
 * identity, so the key stays here too, for the person to try again once
 * the ban has lapsed. The fingerprint goes along when the caller has one,
 * so a ban on this device at its address is seen the way /api/session
 * sees it.
 * @param {{ base?: string, fetchImpl?: typeof fetch, fingerprint?: string }} [opts]
 * @returns {Promise<boolean|'banned'>} whether the server confirmed; 'banned' when it refused and nothing was cleared
 */
export async function forgetKeepsake({ base = '', fetchImpl = globalThis.fetch, fingerprint = '' } = {}) {
  const ks = getKeepsake();
  let confirmed = false;
  if (ks && typeof fetchImpl === 'function') {
    try {
      const res = await fetchImpl(`${base}/api/keepsake/forget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: ks.key, ...(fingerprint ? { fingerprint } : {}) }),
      });
      if (res && res.status === 403) return 'banned';
      confirmed = !!res && res.ok === true;
    } catch {
      confirmed = false;
    }
  }
  clearKeepsake();
  return confirmed;
}

/** One JSON POST; a network failure reads as status 0, never a throw. */
async function post(fetchImpl, base, path, body) {
  try {
    const res = await fetchImpl(`${base}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = res && typeof res.json === 'function' ? await res.json().catch(() => ({})) : {};
    return { status: res ? res.status : 0, data: data && typeof data === 'object' ? data : {} };
  } catch {
    return { status: 0, data: {} };
  }
}

/**
 * What the server remembers for the key this device holds -- the id, the
 * band, the dates, and the username when it has one -- or null: no key here,
 * or a key the server no longer honours.
 * @param {{ base?: string, fetchImpl?: typeof fetch }} [opts]
 */
export async function keepsakeMe({ base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks || typeof fetchImpl !== 'function') return null;
  try {
    const res = await fetchImpl(`${base}/api/keepsake/me`, { headers: { 'X-Lumi-Keepsake': ks.key } });
    if (!res || res.status !== 200) return null;
    const data = await res.json();
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/**
 * Put a username and a password on the keepsake this device holds. The
 * answer carries the recovery code exactly once; the caller shows it as a
 * step, as the sign-up form does.
 * @param {{ username: string, password: string, base?: string, fetchImpl?: typeof fetch }} opts
 * @returns {Promise<{ status: number, data: object }>}
 */
export async function nameKeepsake({ username, password, base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks || typeof fetchImpl !== 'function') return { status: 0, data: {} };
  return post(fetchImpl, base, '/api/keepsake/name', { key: ks.key, username, password });
}

/**
 * Sign in to a named keepsake. On success the key the server issued to THIS
 * device is stored, replacing whatever was here; a refusal stores nothing.
 * The key this device already holds goes along when it has one: a device the
 * server knows in the other band is refused.
 * @param {{ username: string, password: string, base?: string, fetchImpl?: typeof fetch }} opts
 * @returns {Promise<{ status: number, data: object }>}
 */
export async function loginKeepsake({ username, password, base = '', fetchImpl = globalThis.fetch } = {}) {
  if (typeof fetchImpl !== 'function') return { status: 0, data: {} };
  const held = getKeepsake();
  const out = await post(fetchImpl, base, '/api/keepsake/login', { username, password, ...(held ? { keepsake: held.key } : {}) });
  if (out.status === 200 && out.data.keepsake) storeKeepsake(out.data.keepsake);
  return out;
}

/**
 * A new password with the recovery code, which signs this device in as well:
 * the answer is a sign-in's, plus the NEW recovery code, once.
 * @param {{ username: string, code: string, password: string, base?: string, fetchImpl?: typeof fetch }} opts
 * @returns {Promise<{ status: number, data: object }>}
 */
export async function recoverKeepsake({ username, code, password, base = '', fetchImpl = globalThis.fetch } = {}) {
  if (typeof fetchImpl !== 'function') return { status: 0, data: {} };
  const held = getKeepsake();
  const out = await post(fetchImpl, base, '/api/keepsake/recover', { username, code, password, ...(held ? { keepsake: held.key } : {}) });
  if (out.status === 200 && out.data.keepsake) storeKeepsake(out.data.keepsake);
  return out;
}

/**
 * Whether the server still honours the key this device holds: 'live', 'gone'
 * (signed out, or forgotten), or 'unknown' when it could not be asked. Only
 * 'gone' is a reason to let go of the key.
 * @returns {Promise<'live'|'gone'|'unknown'>}
 */
export async function keepsakeStatus({ base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks) return 'gone';
  if (typeof fetchImpl !== 'function') return 'unknown';
  try {
    const res = await fetchImpl(`${base}/api/keepsake/me`, { headers: { 'X-Lumi-Keepsake': ks.key } });
    if (res && res.status === 200) return 'live';
    if (res && res.status === 404) return 'gone';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/** The devices signed in to this device's keepsake, this one first, or null. */
export async function keepsakeDevices({ base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks || typeof fetchImpl !== 'function') return null;
  const out = await post(fetchImpl, base, '/api/keepsake/devices', { key: ks.key });
  return out.status === 200 && Array.isArray(out.data.devices) ? out.data.devices : null;
}

/** Sign one device out of the name; when it is this one, the key here goes too. */
export async function signOutKeepsakeDevice({ id, base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks || typeof fetchImpl !== 'function') return { status: 0, data: {} };
  const out = await post(fetchImpl, base, '/api/keepsake/devices/sign-out', { key: ks.key, id });
  if (out.status === 200 && out.data.self) clearKeepsake();
  return out;
}

/** Every device but this one, signed out of the name. */
export async function signOutKeepsakeOthers({ base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks || typeof fetchImpl !== 'function') return { status: 0, data: {} };
  return post(fetchImpl, base, '/api/keepsake/devices/sign-out-others', { key: ks.key });
}

/** A new password for the name, with the current one; every other device is signed out. */
export async function changeKeepsakePassword({ current, password, base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks || typeof fetchImpl !== 'function') return { status: 0, data: {} };
  return post(fetchImpl, base, '/api/keepsake/password', { key: ks.key, current, password });
}

/** The notice of a sign-in from another device, dismissed. */
export async function ackKeepsakeNotice({ base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  if (!ks || typeof fetchImpl !== 'function') return { status: 0, data: {} };
  return post(fetchImpl, base, '/api/keepsake/notice', { key: ks.key });
}

export default {
  getKeepsake,
  storeKeepsake,
  clearKeepsake,
  forgetKeepsake,
  keepsakeMe,
  nameKeepsake,
  loginKeepsake,
  recoverKeepsake,
  keepsakeStatus,
  keepsakeDevices,
  signOutKeepsakeDevice,
  signOutKeepsakeOthers,
  changeKeepsakePassword,
  ackKeepsakeNotice,
};
